/**
 * @file Purchase PPD photos component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import get from 'lodash.get';

import NoPhotosFound from './NoPhotosFound';
import Button from '../global/Button';
import {
  getPpdViewerHelperStatusFromStore,
  setPpdViewerHelperHiddenOnStore,
} from '../../util/purchase-album-photos';
import { getAlbumPrice } from '../../util/purchase-album';
import { formatPriceForDisplay } from '../../util/format-money';
import PhotoViewerHelpText from './PhotoViewerHelpText';
import SubscriptionDescriptionModal from './SubscriptionDescriptionModal';
import {
  PURCHASE_PPD_TYPE_MODAL,
  INDIVIDUAL_PHOTO_NOTICE_MODAL,
  PPD_PURCHASE_PURCHASE_OPTIONS,
} from '../../constants/modals';
import { PPD_CART_ITEM_TYPES } from '../../constants/ppd-purchase';
import PurchasePpdItemPurchaseModalContainer from '../../containers/purchase/PurchasePpdItemPurchaseModalContainer';
import PurchasePpdPrintPackageModalContainer from '../../containers/purchase/PurchasePpdPrintPackageModalContainer';
import PurchasePpdIndividualPrintModalContainer from '../../containers/purchase/PurchasePpdIndividualPrintModalContainer';
import {
  isIndividualPrintPurchased,
  isPrintPackagePurchased,
  isPpdDownloadPriceFromCustomTag,
  sortMatchedPhotosByFacebox,
  isCartItemSelected,
  getPriceWithoutAddons,
  getSelectedIdentityPurchasedCustomPrints,
} from '../../util/ppd-purchase';
import PhotoViewerSwitch from './PhotoViewerSwitch';
import PurchasePpdPhotosPhotoItemContainer from '../../containers/purchase/PurchasePpdPhotosPhotoItemContainer';
import { isPpd, isPpa } from '../../util/album';
import IndividualPhotoWarningModal from './IndividualPhotoWarningModal';
import { getInitialActiveIdentityForPpdPurchase } from '../../util/identity';
import PurchasePpdIdentityFilter from './PurchasePpdIdentityFilter';
import {
  getAlbumCustomPrint,
  getAlbumCustomPrintPrice,
} from '../../util/custom-prints';
import PurchasedCustomPrintConfigNotice from './custom-prints/PurchasedCustomPrintConfigNotice';
import PurchasePpdOptionsModal from './PurchasePpdOptionsModal';

class PurchasePpdPhotos extends Component {
  static propTypes = {
    matchedPhotos: PropTypes.arrayOf(
      PropTypes.shape({
        comparedFaces: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        photo: PropTypes.shape({
          id: PropTypes.string,
          thumbnail_urls: PropTypes.arrayOf(
            PropTypes.shape({
              width: PropTypes.number,
              height: PropTypes.number,
              url: PropTypes.string,
            }),
          ),
        }),
      }),
    ),
    entitledPhotos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    entitledPhotosNo: PropTypes.number,
    album: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      default_price_per_photo: PropTypes.number,
      is_demo: PropTypes.bool.isRequired,
      album_folder: PropTypes.shape({
        name: PropTypes.string,
      }),
      subscription_album_folder: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        price: PropTypes.shape({
          value: PropTypes.number,
        }),
      }),
      creator: PropTypes.object,
    }),
    cart: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.keys(PPD_CART_ITEM_TYPES)),
        photo: PropTypes.shape({
          id: PropTypes.string,
        }),
        printSize: PropTypes.shape({
          id: PropTypes.string,
        }),
        printPackage: PropTypes.shape({
          id: PropTypes.string,
        }),
        quantity: PropTypes.number,
      }),
    ),
    match: PropTypes.shape({
      url: PropTypes.string,
    }),
    loading: PropTypes.bool,
    hasPpaMode: PropTypes.bool,
    openModal: PropTypes.func.isRequired,
    price: PropTypes.shape({
      total_price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      total_shipping_cost: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      total_tax: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
    }),
    loadingPrice: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
    checkoutSuccess: PropTypes.bool,
    user: PropTypes.shape({
      id: PropTypes.string,
      purchased_print_packages: PropTypes.shape({
        total: PropTypes.number,
      }),
    }),
    toggleCartItem: PropTypes.func.isRequired,
    selectSubscription: PropTypes.func.isRequired,
    deselectSubscription: PropTypes.func.isRequired,
    selectAlbumCustomPrint: PropTypes.func.isRequired,
    deselectAlbumCustomPrint: PropTypes.func.isRequired,
    activeIdentity: PropTypes.shape({
      id: PropTypes.string,
    }),
    purchasedIdentities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    selectIdentity: PropTypes.func.isRequired,
    selectedIdentities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        identity: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    ),
    pendingPurchasedCustomPrints: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        photos_no: PropTypes.number,
        selected_identity: PropTypes.shape({
          id: PropTypes.string.isRequired,
        }).isRequired,
      }),
    ),
  };

  state = {
    helpTextVisible: false,
  };

  componentDidMount() {
    const { activeIdentity, selectedIdentities } = this.props;
    const storeHelperStatus = getPpdViewerHelperStatusFromStore();

    if (!storeHelperStatus) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState(() => ({
        helpTextVisible: true,
      }));
    }

    if (!activeIdentity && selectedIdentities) {
      this.onSetInitialActiveIdentity();
    }
  }

  componentDidUpdate() {
    const {
      checkoutSuccess,
      history,
      match,
      activeIdentity,
      selectedIdentities,
    } = this.props;

    if (checkoutSuccess) {
      history.push(`${match.url}/success`);
    }

    if (!activeIdentity && selectedIdentities) {
      this.onSetInitialActiveIdentity();
    }
  }

  onSetInitialActiveIdentity = () => {
    const { selectIdentity, selectedIdentities } = this.props;
    if (
      selectedIdentities.some(
        selectedIdentity => !selectedIdentity.matched_photos_extended,
      )
    ) {
      return;
    }

    const activeIdentity = getInitialActiveIdentityForPpdPurchase(
      selectedIdentities,
    );

    if (!activeIdentity) {
      return;
    }

    // Select only relevant data and not the
    // entire object containing identity matched photos etc
    selectIdentity({
      id: activeIdentity.id,
      identity: activeIdentity.identity,
    });
  };

  onPhotoTouched = () => {
    const { helpTextVisible } = this.state;

    if (helpTextVisible) {
      setPpdViewerHelperHiddenOnStore();
      this.setState(() => ({ helpTextVisible: false }));
    }
  };

  onPhotoClicked = (photoId, isDownloadEntitled) => {
    const { history, album } = this.props;

    if (isDownloadEntitled) {
      history.push(`/purchase/album/${album.id}/view/${photoId}`);
    }
  };

  onGoToCheckout = () => {
    const { history, album } = this.props;
    history.push(`/purchase/album/${album.id}/photos/checkout`);
  };

  isPhotoSelected = photoId => {
    const { cart } = this.props;

    return !!cart.find(cartItem => get(cartItem, 'photo.id') === photoId);
  };

  isIdentitySelectedForSubscription = () => {
    const { cart, activeIdentity } = this.props;

    if (!activeIdentity) {
      return false;
    }

    return isCartItemSelected(
      cart,
      activeIdentity.id,
      PPD_CART_ITEM_TYPES.SUBSCRIPTION,
    );
  };

  activeIdentityHasCustomPrint = () => {
    const { cart, activeIdentity, album } = this.props;
    const albumCustomPrint = getAlbumCustomPrint(album);

    if (!activeIdentity || !albumCustomPrint) {
      return false;
    }

    return isCartItemSelected(
      cart,
      `${activeIdentity.id}-${albumCustomPrint.id}`,
      PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT,
    );
  };

  toggleIdentityForSubscription = () => {
    const {
      selectSubscription,
      deselectSubscription,
      activeIdentity,
    } = this.props;

    if (this.isIdentitySelectedForSubscription()) {
      deselectSubscription(activeIdentity);
    } else {
      selectSubscription(activeIdentity);
    }
  };

  toggleIdentityForCustomPrintPurchase = () => {
    const {
      selectAlbumCustomPrint,
      deselectAlbumCustomPrint,
      activeIdentity,
      album,
    } = this.props;
    const albumCustomPrint = getAlbumCustomPrint(album);

    if (this.activeIdentityHasCustomPrint()) {
      deselectAlbumCustomPrint(activeIdentity, albumCustomPrint);
    } else {
      selectAlbumCustomPrint(activeIdentity, albumCustomPrint);
    }
  };

  activeIdentityHasPurchasedSubscription = () => {
    const { activeIdentity, purchasedIdentities } = this.props;

    return !!purchasedIdentities.find(
      identity => get(identity, 'id') === get(activeIdentity, 'identity.id'),
    );
  };

  /**
   * Determines whether the checkout actions block should be shown or not.
   *
   * @returns {boolean}
   */
  shouldDisplayCheckoutActions = () => {
    const {
      matchedPhotos,
      cart,
      album,
      hasPpaMode,
      activeIdentity,
    } = this.props;

    const isActiveIdentityPurchased = this.activeIdentityHasPurchasedSubscription();
    const albumCustomPrint = getAlbumCustomPrint(album);

    return (
      (hasPpaMode && !isActiveIdentityPurchased && activeIdentity) ||
      (isPpd(album) && matchedPhotos.length > 0) ||
      !!albumCustomPrint ||
      // We should allow users to check cart if they have it.
      cart.length > 0
    );
  };

  getAlbumCustomPrintPriceForIdentity = () => {
    const { album } = this.props;
    const activeIdentityHasSubscription =
      this.activeIdentityHasPurchasedSubscription() ||
      this.isIdentitySelectedForSubscription();
    const albumCustomPrint = getAlbumCustomPrint(album);

    if (!albumCustomPrint) {
      return null;
    }

    const price = getAlbumCustomPrintPrice(
      albumCustomPrint,
      activeIdentityHasSubscription &&
        !!get(albumCustomPrint, 'discount_price.value'),
    );

    return price;
  };

  renderCheckoutButtonText = () => {
    const { cart = [], price, loadingPrice } = this.props;

    if (loadingPrice) {
      return 'Calculating...';
    }

    const itemsAmount = cart.length;
    const photoLabel = pluralize('Item', itemsAmount);
    const priceWithoutAddons = getPriceWithoutAddons(price);
    const formattedPrice = formatPriceForDisplay(priceWithoutAddons, true);

    return itemsAmount ? (
      <span>
        Checkout <br />{' '}
        <span className="text-xs">
          ({itemsAmount} {photoLabel} for ${formattedPrice})
        </span>
      </span>
    ) : (
      'Buy Individual Photos'
    );
  };

  render() {
    const {
      matchedPhotos,
      cart,
      album,
      loading,
      hasPpaMode,
      entitledPhotosNo,
      openModal,
      loadingPrice,
      entitledPhotos,
      user,
      toggleCartItem,
      activeIdentity,
      selectedIdentities,
      selectIdentity,
      selectAlbumCustomPrint,
      pendingPurchasedCustomPrints,
    } = this.props;

    const { helpTextVisible } = this.state;

    if (loading || !album || !activeIdentity) {
      return (
        <div className="p-5 mt-5 mb-5 text-center flex-auto">
          <span className="spinner" />
        </div>
      );
    }

    const matchedPhotosSortedByFacebox = sortMatchedPhotosByFacebox(
      matchedPhotos,
    );
    const isActiveIdentityPurchased = this.activeIdentityHasPurchasedSubscription();
    const albumCustomPrint = getAlbumCustomPrint(album);

    const displayCheckoutActions = this.shouldDisplayCheckoutActions();
    const activeIdentityPurchasedCustomPrints = getSelectedIdentityPurchasedCustomPrints(
      activeIdentity,
      pendingPurchasedCustomPrints,
    );

    const showPurchasingOptionsButton =
      (hasPpaMode && !isActiveIdentityPurchased && activeIdentity) ||
      albumCustomPrint;

    return (
      <Fragment>
        <div className="content-wrap-gray flex-auto d-flex flex-column">
          {entitledPhotosNo > 0 && !isPpa(album) && (
            <PhotoViewerSwitch albumId={album.id} activeTab="ppd" />
          )}
          <PurchasePpdIdentityFilter
            selectIdentity={selectIdentity}
            selectedIdentities={selectedIdentities}
            activeIdentity={activeIdentity}
            pendingPurchasedCustomPrints={pendingPurchasedCustomPrints}
            album={album}
          />

          {activeIdentityPurchasedCustomPrints.length > 0 && (
            <div className="p-1">
              <PurchasedCustomPrintConfigNotice
                purchasedCustomPrints={activeIdentityPurchasedCustomPrints}
                album={album}
              />
            </div>
          )}

          {matchedPhotos.length > 0 && activeIdentity ? (
            <Fragment>
              <div className="pos-rel px-1 d-flex">
                <PhotoViewerHelpText visible={helpTextVisible} />
              </div>

              <div className="pos-rel pb-0 p-1">
                <div className="photo-list-fixed-width">
                  {matchedPhotosSortedByFacebox.map(photoItem => {
                    const isSelected = this.isPhotoSelected(photoItem.photo.id);
                    const isDownloadEntitled = !!entitledPhotos.find(
                      ({ id }) => id === photoItem.photo.id,
                    );
                    // Individual prints & packages messages disabled at the moment since we
                    // don't really support individual prints as of now.
                    const isIndividualPrintPurchasedBool =
                      isIndividualPrintPurchased(photoItem.photo, user) &&
                      false;
                    const isPrintPackagePurchasedBool =
                      isPrintPackagePurchased(photoItem.photo, user) && false;
                    const showPricingTag = isPpdDownloadPriceFromCustomTag(
                      photoItem.photo,
                      album,
                    );

                    return (
                      <PurchasePpdPhotosPhotoItemContainer
                        key={photoItem.photo.id}
                        photoItem={photoItem}
                        isDownloadEntitled={isDownloadEntitled}
                        isSelected={isSelected}
                        isIndividualPrintPurchased={
                          isIndividualPrintPurchasedBool
                        }
                        isPrintPackagePurchased={isPrintPackagePurchasedBool}
                        onClick={() => {
                          openModal(PURCHASE_PPD_TYPE_MODAL, photoItem);
                        }}
                        onPhotoClicked={() =>
                          this.onPhotoClicked(photoItem.id, isDownloadEntitled)
                        }
                        onPhotoTouched={this.onPhotoTouched}
                        showPricingTag={showPricingTag}
                        disableSelection={!isPpd(album)}
                      />
                    );
                  })}
                </div>
              </div>
            </Fragment>
          ) : (
            <NoPhotosFound
              album={album}
              subscriptionPrice={getAlbumPrice(album)}
              hasPpaMode={!isActiveIdentityPurchased && hasPpaMode}
              entitledPhotosNo={entitledPhotosNo}
              onPurchase={this.toggleIdentityForSubscription}
            />
          )}

          {displayCheckoutActions && (
            <div className="ppd-photos-toolbar">
              {showPurchasingOptionsButton && (
                <Button
                  btnSize="sm"
                  btnBlock
                  btnStyle="yellow"
                  className="text-sm px-1"
                  onClick={() => openModal(PPD_PURCHASE_PURCHASE_OPTIONS)}
                  disabled={loadingPrice}
                >
                  See purchasing options...
                </Button>
              )}
              {(isPpd(album) || cart.length > 0) && (
                <Button
                  btnSize="sm"
                  btnBlock
                  className={`text-sm px-1 ${
                    cart.length === 0 ? 'disabled' : ''
                  }`}
                  onClick={
                    cart.length > 0
                      ? this.onGoToCheckout
                      : () => openModal(INDIVIDUAL_PHOTO_NOTICE_MODAL)
                  }
                  disabled={loadingPrice}
                >
                  {this.renderCheckoutButtonText()}
                </Button>
              )}
            </div>
          )}
        </div>

        <SubscriptionDescriptionModal
          album={album}
          onSubscriptionPurchase={this.toggleIdentityForSubscription}
          onCustomPrintPurchase={() =>
            selectAlbumCustomPrint(activeIdentity, albumCustomPrint)
          }
        />
        <IndividualPhotoWarningModal />
        <PurchasePpdItemPurchaseModalContainer
          toggleCartItem={toggleCartItem}
          entitledPhotos={entitledPhotos}
        />
        <PurchasePpdPrintPackageModalContainer
          toggleCartItem={toggleCartItem}
        />
        <PurchasePpdIndividualPrintModalContainer
          toggleCartItem={toggleCartItem}
        />
        <PurchasePpdOptionsModal
          album={album}
          loading={loading}
          hasPpaMode={hasPpaMode}
          openModal={openModal}
          activeIdentity={activeIdentity}
          selectAlbumCustomPrint={selectAlbumCustomPrint}
          isActiveIdentityPurchased={isActiveIdentityPurchased}
          isActiveIdentitySelectedForSubscription={this.isIdentitySelectedForSubscription()}
          activeIdentityHasCustomPrint={this.activeIdentityHasCustomPrint()}
          toggleIdentityForCustomPrintPurchase={
            this.toggleIdentityForCustomPrintPurchase
          }
          toggleIdentityForSubscription={this.toggleIdentityForSubscription}
        />
      </Fragment>
    );
  }
}

export default PurchasePpdPhotos;
